/*
  Passport Baits Logic
*/

const PassportScrollLock = (() => {
  const reasons = new Set();
  let lockedScrollY = 0;
  let previousStyles = null;

  const lockPage = () => {
    if (previousStyles || !document.body) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    lockedScrollY = window.scrollY || html.scrollTop || body.scrollTop || 0;
    previousStyles = {
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight
    };

    html.classList.add('passport-scroll-locked');
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
  };

  const unlockPage = () => {
    if (!previousStyles || !document.body) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = lockedScrollY;

    html.classList.remove('passport-scroll-locked');
    html.style.overflow = previousStyles.htmlOverflow;
    html.style.overscrollBehavior = previousStyles.htmlOverscrollBehavior;
    body.style.overflow = previousStyles.bodyOverflow;
    body.style.overscrollBehavior = previousStyles.bodyOverscrollBehavior;
    body.style.position = previousStyles.bodyPosition;
    body.style.top = previousStyles.bodyTop;
    body.style.left = previousStyles.bodyLeft;
    body.style.right = previousStyles.bodyRight;
    body.style.width = previousStyles.bodyWidth;
    body.style.paddingRight = previousStyles.bodyPaddingRight;

    previousStyles = null;
    window.scrollTo(0, scrollY);
  };

  const set = (reason, shouldLock) => {
    if (shouldLock) {
      reasons.add(reason);
      lockPage();
      return;
    }

    reasons.delete(reason);

    if (reasons.size === 0) {
      unlockPage();
    }
  };

  return {
    set,
    isLocked: () => reasons.size > 0
  };
})();

window.PassportScrollLock = PassportScrollLock;

const registerPassportAlpine = () => {
  if (!window.Alpine || window.__passportAlpineRegistered) return;

  window.__passportAlpineRegistered = true;

  window.Alpine.data('passportHeader', (initialItemCount = 0) => ({
    mobileMenuOpen: false,
    mobileSubmenu: null,
    cartOpen: false,
    searchOpen: false,
    cart: { item_count: Number(initialItemCount) || 0, items: [] },
    loadingCart: false,
    cartError: null,
    toast: null,
    init() {
      this.$watch('mobileMenuOpen', () => this.syncDrawerScrollLock());
      this.$watch('cartOpen', () => this.syncDrawerScrollLock());
      this.syncDrawerScrollLock();
    },
    syncDrawerScrollLock() {
      PassportScrollLock.set('header-drawer', this.mobileMenuOpen || this.cartOpen);
    },
    openMobileMenu() {
      this.mobileMenuOpen = true;
      this.mobileSubmenu = null;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.mobileSubmenu = null;
    },
    toggleMobileSubmenu(id) {
      this.mobileSubmenu = this.mobileSubmenu === id ? null : id;
    },
    showToast(message, type = 'error') {
      this.toast = { message, type };
      setTimeout(() => { this.toast = null; }, 4000);
    },
    fetchCart() {
      this.loadingCart = true;

      return fetch(window.Shopify.routes.root + 'cart.js')
        .then((res) => res.json())
        .then((cart) => {
          this.cart = cart;
          this.loadingCart = false;
        })
        .catch(() => {
          this.loadingCart = false;
          this.showToast('Unable to refresh cart');
        });
    },
    addToCart(id, qty = 1) {
      this.loadingCart = true;
      this.cartError = null;

      return fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: qty }] })
      })
        .then(async (res) => {
          if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.description || 'Failed to add to cart');
          }

          return res.json();
        })
        .then(() => {
          this.fetchCart();
          this.cartOpen = true;
        })
        .catch((err) => {
          this.showToast(err.message, 'error');
          this.loadingCart = false;
        });
    },
    removeItem(key) {
      this.updateQuantity(key, 0);
    },
    updateQuantity(key, qty) {
      this.loadingCart = true;
      this.cartError = null;

      return fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(async (res) => {
          if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.description || 'Inventory Limit Reached');
          }

          return res.json();
        })
        .then((cart) => {
          this.cart = cart;
          this.loadingCart = false;

          if (qty === 0) {
            this.$dispatch('cart-updated');
          }
        })
        .catch((err) => {
          this.showToast(err.message, 'error');
          this.fetchCart();
        });
    }
  }));

  window.Alpine.data('passportSearch', (options = {}) => ({
    query: '',
    results: [],
    loading: false,
    error: false,
    isOpen: false,
    mobileOpen: false,
    minimumQueryLength: 2,
    searchUrl: options.searchUrl || '/search',
    popularTerms: options.popularTerms || ['MIR', 'Frogs', 'Soft baits', 'Hard baits', 'New arrivals'],
    searchShortcuts: options.searchShortcuts || {},
    fallbackProducts: options.fallbackProducts || [],
    recentSearches: [],
    recentSearchesKey: 'passport-baits-recent-searches',
    activeRequestId: 0,
    abortController: null,
    init() {
      this.popularTerms = this.normalizeTerms(this.popularTerms).slice(0, 6);
      this.searchShortcuts = this.normalizeSearchShortcuts(this.searchShortcuts);
      this.fallbackProducts = this.normalizeFallbackProducts(this.fallbackProducts);
      this.loadRecentSearches();

      this.$watch('mobileOpen', (isOpen) => {
        const shouldLock = Boolean(isOpen);
        document.documentElement.classList.toggle('passport-search-lock', shouldLock);

        if (window.PassportScrollLock) {
          window.PassportScrollLock.set('search-drawer', shouldLock);
        }

        this.syncSearchLayer();
      });
      this.$watch('isOpen', () => this.syncSearchLayer());
      this.syncSearchLayer();
    },
    get trimmedQuery() {
      return this.query.trim();
    },
    openResults() {
      this.isOpen = true;
    },
    closeResults() {
      this.isOpen = false;
    },
    syncSearchLayer() {
      this.$dispatch('passport-search-toggle', {
        open: Boolean(this.mobileOpen || this.isOpen)
      });
    },
    normalizeTerms(terms) {
      const source = Array.isArray(terms) ? terms : String(terms || '').split(',');
      const seen = new Set();

      return source
        .map((term) => String(term || '').trim())
        .filter((term) => {
          const key = term.toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    },
    normalizeShortcutKey(term) {
      return String(term || '').trim().toLowerCase();
    },
    normalizeSearchShortcuts(shortcuts) {
      if (!shortcuts || typeof shortcuts !== 'object') return {};

      return Object.entries(shortcuts).reduce((normalizedShortcuts, [term, url]) => {
        const key = this.normalizeShortcutKey(term);
        const href = String(url || '').trim();
        if (key && href) normalizedShortcuts[key] = href;
        return normalizedShortcuts;
      }, {});
    },
    shortcutUrlForTerm(term) {
      return this.searchShortcuts[this.normalizeShortcutKey(term)] || '';
    },
    loadRecentSearches() {
      try {
        const storedTerms = JSON.parse(window.localStorage.getItem(this.recentSearchesKey) || '[]');
        this.recentSearches = this.normalizeTerms(storedTerms).slice(0, 5);
      } catch (err) {
        this.recentSearches = [];
      }
    },
    persistRecentSearches() {
      try {
        window.localStorage.setItem(this.recentSearchesKey, JSON.stringify(this.recentSearches));
      } catch (err) {
        this.recentSearches = [];
      }
    },
    rememberSearchTerm(term = this.trimmedQuery) {
      const normalizedTerm = String(term || '').trim();
      if (normalizedTerm.length < this.minimumQueryLength) return;

      this.recentSearches = [
        normalizedTerm,
        ...this.recentSearches.filter((recentTerm) => recentTerm.toLowerCase() !== normalizedTerm.toLowerCase())
      ].slice(0, 5);
      this.persistRecentSearches();
    },
    clearRecentSearches() {
      this.recentSearches = [];

      try {
        window.localStorage.removeItem(this.recentSearchesKey);
      } catch (err) {
        this.recentSearches = [];
      }

      this.$nextTick(() => {
        const focusTarget = this.mobileOpen ? this.$refs.mobileInput : this.$refs.input;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      });
    },
    selectSearchTerm(term) {
      const selectedTerm = String(term || '').trim();
      const shortcutUrl = this.shortcutUrlForTerm(selectedTerm);

      if (shortcutUrl) {
        this.rememberSearchTerm(selectedTerm);
        window.location.href = shortcutUrl;
        return;
      }

      this.query = selectedTerm;
      this.rememberSearchTerm(this.query);

      this.$nextTick(() => {
        const focusTarget = this.mobileOpen ? this.$refs.mobileInput : this.$refs.input;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        this.fetchResults();
      });
    },
    focusMobileInput() {
      if (this.$refs.mobileInput) {
        this.$refs.mobileInput.focus({ preventScroll: true });
      }
    },
    openMobileSearch() {
      this.mobileOpen = true;
      this.closeResults();

      this.$nextTick(() => {
        this.focusMobileInput();
        window.setTimeout(() => this.focusMobileInput(), 60);

        if (this.trimmedQuery.length >= this.minimumQueryLength && this.results.length === 0 && !this.loading) {
          this.fetchResults();
        }
      });
    },
    closeMobileSearch() {
      this.mobileOpen = false;
    },
    clearSearch() {
      this.activeRequestId += 1;

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.query = '';
      this.results = [];
      this.loading = false;
      this.error = false;
      this.isOpen = !this.mobileOpen;

      this.$nextTick(() => {
        const focusTarget = this.mobileOpen ? this.$refs.mobileInput : this.$refs.input;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      });
    },
    submitSearch(event) {
      if (this.trimmedQuery.length === 0) {
        event.preventDefault();
        this.clearSearch();
        return;
      }

      this.query = this.trimmedQuery;
      const shortcutUrl = this.shortcutUrlForTerm(this.query);
      if (shortcutUrl) {
        event.preventDefault();
        this.rememberSearchTerm();
        window.location.href = shortcutUrl;
        return;
      }

      this.rememberSearchTerm();
    },
    searchAllUrl() {
      const shortcutUrl = this.shortcutUrlForTerm(this.trimmedQuery);
      if (shortcutUrl) return shortcutUrl;

      const params = new URLSearchParams({ q: this.trimmedQuery, type: 'product', 'options[prefix]': 'last' });
      return `${this.searchUrl}?${params.toString()}`;
    },
    queryLabel() {
      return this.trimmedQuery ? `for "${this.trimmedQuery}"` : '';
    },
    resultSummary() {
      if (this.trimmedQuery.length < this.minimumQueryLength) {
        return this.recentSearches.length > 0 ? 'Pick up a recent search or explore popular products' : 'Explore popular products';
      }

      if (this.shortcutUrlForTerm(this.trimmedQuery)) {
        return `Open "${this.trimmedQuery}"`;
      }

      if (this.loading && this.results.length === 0) return 'Checking products';
      if (this.error) return 'Unable to load quick results';
      if (this.results.length === 0) return `No quick matches for "${this.trimmedQuery}"`;
      return `${this.results.length} quick match${this.results.length === 1 ? '' : 'es'} for "${this.trimmedQuery}"`;
    },
    availabilityLabel(product) {
      return product.available ? 'In stock' : 'Check stock';
    },
    resultMeta(product) {
      return [product.vendor, product.country || product.type].filter(Boolean).join(' // ');
    },
    resultCta(product) {
      return product.available ? 'View product' : 'View details';
    },
    formatProductPrice(product) {
      const price = product.price_min || product.price || '';

      if (!price) return '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(String(price));
      }

      return `$${price}`;
    },
    normalizeProduct(product) {
      const rawBody = product.body || '';
      const summary = rawBody
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 92);

      return {
        ...product,
        summary: summary ? `${summary}${summary.length >= 92 ? '...' : ''}` : ''
      };
    },
    normalizeFallbackProducts(products) {
      if (!Array.isArray(products)) return [];

      return products
        .map((product) => ({
          ...product,
          id: String(product.id || product.url || product.title || ''),
          searchText: String(product.searchText || [
            product.title,
            product.vendor,
            product.type,
            product.country,
            product.summary
          ].filter(Boolean).join(' '))
        }))
        .filter((product) => product.id && product.title && product.url);
    },
    fallbackMatches(query, existingProducts = []) {
      const normalizedQuery = String(query || '').trim().toLowerCase();
      if (normalizedQuery.length < this.minimumQueryLength) return [];

      const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
      const seen = new Set(existingProducts.map((product) => String(product.id || product.url)));

      return this.fallbackProducts
        .filter((product) => {
          if (seen.has(String(product.id || product.url))) return false;

          const searchableText = String(product.searchText || '').toLowerCase();
          return queryTokens.every((token) => searchableText.includes(token));
        })
        .slice(0, Math.max(0, 6 - existingProducts.length))
        .map((product) => this.normalizeProduct(product));
    },
    fetchResults() {
      if (this.trimmedQuery.length < this.minimumQueryLength) {
        this.activeRequestId += 1;

        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }

        this.results = [];
        this.loading = false;
        this.error = false;
        this.isOpen = !this.mobileOpen;
        return;
      }

      if (this.shortcutUrlForTerm(this.trimmedQuery)) {
        this.activeRequestId += 1;

        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }

        this.results = [];
        this.loading = false;
        this.error = false;
        this.isOpen = !this.mobileOpen;
        return;
      }

      const requestId = ++this.activeRequestId;
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
      const endpoint = `${root}search/suggest.json?q=${encodeURIComponent(this.trimmedQuery)}&resources[type]=product&resources[limit]=6`;

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.loading = true;
      this.error = false;
      this.isOpen = !this.mobileOpen;

      fetch(endpoint, { signal: this.abortController.signal })
        .then((response) => response.json())
        .then((suggestions) => {
          if (requestId !== this.activeRequestId) return;

          const products = suggestions.resources.results.products || [];
          const normalizedProducts = products.map((product) => this.normalizeProduct(product));
          const fallbackProducts = this.fallbackMatches(this.trimmedQuery, normalizedProducts);
          this.results = [...normalizedProducts, ...fallbackProducts].slice(0, 6);
          this.loading = false;
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          if (requestId !== this.activeRequestId) return;

          const fallbackProducts = this.fallbackMatches(this.trimmedQuery);
          this.results = fallbackProducts;
          this.error = fallbackProducts.length === 0;
          this.loading = false;
        });
    }
  }));
};

document.addEventListener('alpine:init', registerPassportAlpine);
registerPassportAlpine();

const initPassportDesktopMenus = () => {
  const navItems = document.querySelectorAll('.passport-nav-item:has(.passport-mega-menu), .passport-nav-item:has(.passport-compact-menu)');

  navItems.forEach((item) => {
    if (item.dataset.passportMenuBound === 'true') return;

    item.dataset.passportMenuBound = 'true';
    let closeTimer = null;
    const menus = item.querySelectorAll(':scope > .passport-mega-menu, :scope > .passport-compact-menu');

    const openMenu = () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }

      item.classList.add('is-menu-open');
    };

    const closeMenu = () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
      }

      closeTimer = window.setTimeout(() => {
        const focusedInside = item.contains(document.activeElement);
        const hoveredMenu = Array.from(menus).some((menu) => menu.matches(':hover'));

        if (!item.matches(':hover') && !hoveredMenu && !focusedInside) {
          item.classList.remove('is-menu-open');
        }
      }, 420);
    };

    item.addEventListener('pointerenter', openMenu);
    item.addEventListener('pointerleave', closeMenu);
    item.addEventListener('focusin', openMenu);
    item.addEventListener('focusout', closeMenu);

    menus.forEach((menu) => {
      menu.addEventListener('pointerenter', openMenu);
      menu.addEventListener('pointerleave', closeMenu);
    });
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initPassportDesktopMenus();

  const policyDate = document.querySelector('.shopify-policy__body p:first-of-type');

  if (policyDate && /^last updated:/i.test(policyDate.textContent.trim())) {
    policyDate.classList.add('passport-policy-date-pill');
  }
});

document.addEventListener('shopify:section:load', initPassportDesktopMenus);
